import { useMemo, useState } from "react";
import { createTeam } from "../../api/team/teamApi";
import useAutoClearMessage from "../../hooks/useAutoClearMessage";

const WEEKDAY_OPTIONS = ["월", "화", "수", "목", "금", "토", "일"];
const AGE_BAND_OPTIONS = [
  "10대 후반",
  "20대 초반",
  "20대 후반",
  "30대 초반",
  "30대 후반",
  "40대 이상",
];
const GENDER_TYPE_OPTIONS = [
  { value: "MIXED", label: "혼성" },
  { value: "MALE", label: "남성" },
  { value: "FEMALE", label: "여성" },
];

function ClientTeamCreatePage({ currentUser }) {
  const fixedChairName = (
    currentUser?.nickname ||
    currentUser?.email ||
    "로그인 사용자"
  ).trim();
  const [formState, setFormState] = useState({
    name: "",
    activityWeekdays: [],
    startTime: "",
    endTime: "",
    ageBand: "",
    genderType: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  useAutoClearMessage(formError, setFormError);

  const timeRangeText = useMemo(
    () => `${formState.startTime} ~ ${formState.endTime}`,
    [formState.endTime, formState.startTime],
  );

  const handleChange = (field, value) => {
    setFormState((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleToggleWeekday = (weekday) => {
    setFormState((previous) => {
      const isSelected = previous.activityWeekdays.includes(weekday);
      if (isSelected) {
        if (previous.activityWeekdays.length === 1) {
          return previous;
        }
        return {
          ...previous,
          activityWeekdays: previous.activityWeekdays.filter(
            (item) => item !== weekday,
          ),
        };
      }

      return {
        ...previous,
        activityWeekdays: [...previous.activityWeekdays, weekday],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!formState.name.trim()) {
      setFormError("팀 이름을 입력해 주세요.");
      return;
    }

    if (!fixedChairName) {
      setFormError("로그인 사용자 정보에서 회장 이름을 확인할 수 없습니다.");
      return;
    }

    if (!formState.activityWeekdays.length) {
      setFormError("최소 1개 이상의 활동 요일을 선택해 주세요.");
      return;
    }

    if (formState.startTime >= formState.endTime) {
      setFormError("종료 시간은 시작 시간보다 늦어야 합니다.");
      return;
    }

    try {
      setIsSubmitting(true);

      await createTeam({
        name: formState.name.trim(),
        chairName: fixedChairName,
        activityWeekdays: formState.activityWeekdays,
        timeRange: timeRangeText,
        ageBand: formState.ageBand,
        genderType: formState.genderType,
        description: formState.description.trim() || null,
      });
      window.location.replace("/team");
    } catch (error) {
      setFormError(
        error?.message || "팀 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main
      className="min-h-screen px-6 py-8 md:px-10 md:py-10"
      style={{
        background:
          "radial-gradient(circle at 8% 0%, #dcfce7 0, transparent 30%), radial-gradient(circle at 100% 10%, #dbeafe 0, transparent 34%), linear-gradient(160deg, #f8fafc 0%, #eef2ff 100%)",
      }}
    >
      <div className="mx-auto flex w-full max-w-[860px] flex-col gap-6">
        <header className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_16px_40px_rgba(15,23,42,0.08)] backdrop-blur-sm">
          <p className="m-0 inline-block rounded-full bg-[#111827] px-3 py-1 text-xs font-bold tracking-[0.08em] text-white">
            TEAM CREATE
          </p>
          <h1 className="mt-3 text-[clamp(24px,4.5vw,34px)] font-semibold leading-tight tracking-[-0.03em] text-[#0f172a]">
            새 팀 등록
          </h1>
          <p className="mt-2 text-sm text-[#475467]">
            팀을 생성하면 등록한 사용자가 기본 회장 권한으로 시작할 수 있습니다.
          </p>
          <p className="mt-2 text-xs text-[#667085]">
            팀 로고는 팀 생성 후 팀 상세 &gt; 팀관리 메뉴에서 등록/수정할 수 있습니다.
          </p>
        </header>

        <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
          <form className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0" onSubmit={handleSubmit}>
            <div>
              <label
                className="mb-2 block text-sm font-semibold text-[#344054]"
                htmlFor="team-name"
              >
                팀 이름 <span className="text-[#d92d20]">*</span>
              </label>
              <input
                id="team-name"
                type="text"
                value={formState.name}
                onChange={(event) => handleChange("name", event.target.value)}
                className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                placeholder="예: AH SHOOT FC"
              />
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-semibold text-[#344054]"
                htmlFor="team-chair-fixed"
              >
                회장 <span className="text-[#d92d20]">*</span>
              </label>
              <div
                id="team-chair-fixed"
                className="flex h-11 items-center rounded-lg border border-[#d0d5dd] bg-[#f9fafb] px-3 text-sm font-semibold text-[#344054]"
              >
                {fixedChairName}
              </div>
              <p className="mt-1 text-xs text-[#667085]">
                로그인 계정 이름으로 자동 고정됩니다.
              </p>
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-semibold text-[#344054]"
                htmlFor="team-age-band"
              >
                평균 연령대 <span className="text-[#d92d20]">*</span>
              </label>
              <select
                id="team-age-band"
                value={formState.ageBand}
                onChange={(event) =>
                  handleChange("ageBand", event.target.value)
                }
                className="h-11 w-full rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm outline-none focus:border-[#667085]"
              >
                {AGE_BAND_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="min-w-0 overflow-hidden">
              <label
                className="mb-2 block text-sm font-semibold text-[#344054]"
                htmlFor="team-start-time"
              >
                시작 시간 <span className="text-[#d92d20]">*</span>
              </label>
              <input
                id="team-start-time"
                type="time"
                value={formState.startTime}
                onChange={(event) =>
                  handleChange("startTime", event.target.value)
                }
                className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
              />
            </div>

            <div className="min-w-0 overflow-hidden">
              <label
                className="mb-2 block text-sm font-semibold text-[#344054]"
                htmlFor="team-end-time"
              >
                종료 시간 <span className="text-[#d92d20]">*</span>
              </label>
              <input
                id="team-end-time"
                type="time"
                value={formState.endTime}
                onChange={(event) =>
                  handleChange("endTime", event.target.value)
                }
                className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
              />
            </div>

            <div className="md:col-span-2">
              <p className="mb-2 text-sm font-semibold text-[#344054]">
                주 활동 요일 <span className="text-[#d92d20]">*</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {WEEKDAY_OPTIONS.map((weekday) => {
                  const selected = formState.activityWeekdays.includes(weekday);
                  return (
                    <button
                      key={weekday}
                      type="button"
                      onClick={() => handleToggleWeekday(weekday)}
                      className={`h-9 rounded-lg px-3 text-sm font-semibold ${
                        selected
                          ? "bg-[#111827] text-white"
                          : "border border-[#d0d5dd] bg-white text-[#344054]"
                      }`}
                    >
                      {weekday}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="md:col-span-2">
              <p className="mb-2 text-sm font-semibold text-[#344054]">
                성별 타입 <span className="text-[#d92d20]">*</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {GENDER_TYPE_OPTIONS.map((option) => {
                  const selected = formState.genderType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleChange("genderType", option.value)}
                      className={`h-9 rounded-lg px-3 text-sm font-semibold ${
                        selected
                          ? "bg-[#111827] text-white"
                          : "border border-[#d0d5dd] bg-white text-[#344054]"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="md:col-span-2">
              <label
                className="mb-2 block text-sm font-semibold text-[#344054]"
                htmlFor="team-description"
              >
                팀 소개
              </label>
              <textarea
                id="team-description"
                value={formState.description}
                onChange={(event) =>
                  handleChange("description", event.target.value)
                }
                className="min-h-[110px] w-full rounded-lg border border-[#d0d5dd] px-3 py-2 text-sm outline-none focus:border-[#667085]"
                placeholder="팀 스타일, 모집 포지션, 참여 규칙 등을 적어주세요."
              />
            </div>

            {formError && (
              <p className="md:col-span-2 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                {formError}
              </p>
            )}

            <div className="md:col-span-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => window.location.replace("/team")}
                className="h-11 rounded-lg border border-[#d0d5dd] bg-white px-5 text-sm font-semibold text-[#344054]"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="h-11 rounded-lg bg-[#111827] px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
              >
                {isSubmitting ? "등록 중..." : "팀 등록하기"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}

export default ClientTeamCreatePage;
